import "@/App.css";
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TranslationProvider } from './components/LanguageSelector';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Public Pages
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { Home } from './pages/Home';
import { Services } from './pages/Services';
import { Projects } from './pages/Projects';
import { Process } from './pages/Process';
import { About } from './pages/About';
import { ServiceAreas } from './pages/ServiceAreas';
import { Reviews } from './pages/Reviews';
import { FAQ } from './pages/FAQ';
import { Contact } from './pages/Contact';

// Portal Pages
import { PortalLogin } from './pages/portal/PortalLogin';
import { PortalLayout } from './pages/portal/PortalLayout';
import { Dashboard } from './pages/portal/Dashboard';
import { JobsPipeline } from './pages/portal/JobsPipeline';
import { NewJob } from './pages/portal/NewJob';
import { JobDetail } from './pages/portal/JobDetail';
import { TeamBuilder } from './pages/portal/TeamBuilder';
import { Inventory } from './pages/portal/Inventory';
import { Documents } from './pages/portal/Documents';
import { Accounting } from './pages/portal/Accounting';
import { FinancialOS } from './pages/portal/FinancialOS';
import { BusinessPlan } from './pages/portal/BusinessPlan';
import { Legal } from './pages/portal/Legal';
import { Calendar } from './pages/portal/Calendar';
import { Maintenance } from './pages/portal/Maintenance';
import { Settings } from './pages/portal/Settings';
import { FloorPlans } from './pages/portal/FloorPlans';
import { Clients } from './pages/portal/Clients';
import { Help } from './pages/portal/Help';
import { Updates } from './pages/portal/Updates';
import { FinishLibrary } from './pages/portal/FinishLibrary';
import { Reports } from './pages/portal/Reports';
import { Reviews as PortalReviews } from './pages/portal/Reviews';
import { Liabilities } from './pages/portal/Liabilities';
import { VehicleFleet } from './pages/portal/VehicleFleet';
import { Messages } from './pages/portal/Messages';
import { ProjectManagement } from './pages/portal/ProjectManagement';
import { ProjectProgress } from './pages/portal/ProjectProgress';
import { ContractorManagement } from './pages/portal/ContractorManagement';
import { ProposalBuilder } from './pages/portal/ProposalBuilder';

// Public Pages
import { ClientIntakeForm } from './pages/public/ClientIntakeForm';

// Customer Portal Pages
import { CustomerLogin } from './pages/customer/CustomerLogin';
import { CustomerProjectView } from './pages/customer/CustomerProjectView';

// Employee Portal Pages
import { EmployeeLogin } from './pages/employee/EmployeeLogin';
import { EmployeePortalLayout } from './pages/employee/EmployeePortalLayout';
import { EmployeeDashboard } from './pages/employee/EmployeeDashboard';
import { EmployeeSchedule } from './pages/employee/EmployeeSchedule';
import { EmployeePay } from './pages/employee/EmployeePay';
import { EmployeePerformance } from './pages/employee/EmployeePerformance';
import { EmployeeTraining } from './pages/employee/EmployeeTraining';
import { EmployeeProfile } from './pages/employee/EmployeeProfile';
import { EmployeeMessages } from './pages/employee/EmployeeMessages';

// Contractor Portal Pages
import { ContractorAgreement } from './pages/contractor/ContractorAgreement';
import { ContractorPortalLayout } from './pages/contractor/ContractorPortalLayout';
import { ContractorDocuments } from './pages/contractor/ContractorDocuments';
import { ContractorDashboard } from './pages/contractor/ContractorDashboard';
import { ContractorAuthProvider } from './contexts/ContractorAuthContext';

// Public Share Page (for clients)
import { SharePage } from './pages/SharePage';

// Public Review Submission Page
import { ReviewSubmit } from './pages/ReviewSubmit';

// Legal Policy Pages
import { PrivacyPolicy } from './pages/public/PrivacyPolicy';
import { TermsOfService } from './pages/public/TermsOfService';
import { Accessibility } from './pages/public/Accessibility';
import { CookiePolicy } from './pages/public/CookiePolicy';

// IP Disclosure for President
import { IPDisclosure } from './pages/portal/IPDisclosure';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }

  return children;
};

// Protected Route for President (requires IP disclosure)
const PresidentProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, api } = useAuth();
  const [hasAgreed, setHasAgreed] = React.useState(null);
  const [checking, setChecking] = React.useState(true);
  
  React.useEffect(() => {
    const checkAgreement = async () => {
      if (user?.role === 'president') {
        try {
          const response = await api.get('/auth/ip-disclosure-status');
          setHasAgreed(response.data.has_agreed);
        } catch (error) {
          // If endpoint fails, allow access
          setHasAgreed(true);
        }
      } else {
        setHasAgreed(true);
      }
      setChecking(false);
    };
    
    if (isAuthenticated && user) {
      checkAgreement();
    } else {
      setChecking(false);
    }
  }, [isAuthenticated, user, api]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }

  // President needs to agree to IP disclosure
  if (user?.role === 'president' && hasAgreed === false) {
    return <IPDisclosure />;
  }

  return children;
};

// Public Layout
const PublicLayout = ({ children }) => (
  <>
    <Header />
    <main className="flex-grow">{children}</main>
    <Footer />
  </>
);

// Placeholder components for portal pages not yet built
const PlaceholderPage = ({ title }) => (
  <div className="text-center py-16">
    <h1 className="font-heading text-3xl mb-4">{title}</h1>
    <p className="text-gray-500">Coming soon...</p>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
      <Route path="/projects" element={<PublicLayout><Projects /></PublicLayout>} />
      <Route path="/process" element={<PublicLayout><Process /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
      <Route path="/service-areas" element={<PublicLayout><ServiceAreas /></PublicLayout>} />
      <Route path="/reviews" element={<PublicLayout><Reviews /></PublicLayout>} />
      <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />

      {/* Portal Login (public) */}
      <Route path="/portal/login" element={<PortalLogin />} />

      {/* Customer Portal (public - separate auth) */}
      <Route path="/customer/login" element={<CustomerLogin />} />
      <Route path="/customer/project" element={<CustomerProjectView />} />

      {/* Client Share Page (public) */}
      <Route path="/share/:token" element={<SharePage />} />
      
      {/* Public Review Submission (no auth required) */}
      <Route path="/review/:token" element={<ReviewSubmit />} />
      
      {/* Legal Policy Pages (public) */}
      <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
      <Route path="/terms" element={<PublicLayout><TermsOfService /></PublicLayout>} />
      <Route path="/accessibility" element={<PublicLayout><Accessibility /></PublicLayout>} />
      <Route path="/cookies" element={<PublicLayout><CookiePolicy /></PublicLayout>} />

      {/* Employee Portal Routes (separate auth) */}
      <Route path="/employee/login" element={<EmployeeLogin />} />
      <Route path="/employee" element={<EmployeePortalLayout />}>
        <Route index element={<EmployeeDashboard />} />
        <Route path="schedule" element={<EmployeeSchedule />} />
        <Route path="pay" element={<EmployeePay />} />
        <Route path="performance" element={<EmployeePerformance />} />
        <Route path="training" element={<EmployeeTraining />} />
        <Route path="profile" element={<EmployeeProfile />} />
        <Route path="messages" element={<EmployeeMessages />} />
      </Route>

      {/* Contractor Portal Routes (separate auth with agreement requirement) */}
      <Route path="/contractor/*" element={
        <ContractorAuthProvider>
          <Routes>
            <Route path="agreement" element={<ContractorAgreement />} />
            <Route element={<ContractorPortalLayout />}>
              <Route index element={<ContractorDashboard />} />
              <Route path="schedule" element={<ContractorDashboard />} />
              <Route path="pay" element={<ContractorDashboard />} />
              <Route path="performance" element={<ContractorDashboard />} />
              <Route path="training" element={<ContractorDashboard />} />
              <Route path="documents" element={<ContractorDocuments />} />
              <Route path="messages" element={<ContractorDashboard />} />
              <Route path="profile" element={<ContractorDashboard />} />
            </Route>
          </Routes>
        </ContractorAuthProvider>
      } />

      {/* Protected Portal Routes */}
      <Route
        path="/portal"
        element={
          <PresidentProtectedRoute>
            <PortalLayout />
          </PresidentProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="jobs" element={<JobsPipeline />} />
        <Route path="progress" element={<ProjectProgress />} />
        <Route path="jobs/new" element={<NewJob />} />
        <Route path="jobs/:id" element={<JobDetail />} />
        <Route path="team" element={<TeamBuilder />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="documents" element={<Documents />} />
        <Route path="floor-plans" element={<FloorPlans />} />
        <Route path="clients" element={<Clients />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="maintenance" element={<Maintenance />} />
        <Route path="accounting" element={<Accounting />} />
        <Route path="financial-os" element={<FinancialOS />} />
        <Route path="business-plan" element={<BusinessPlan />} />
        <Route path="legal" element={<Legal />} />
        <Route path="settings" element={<Settings />} />
        <Route path="help" element={<Help />} />
        <Route path="updates" element={<Updates />} />
        <Route path="finish-library" element={<FinishLibrary />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reviews" element={<PortalReviews />} />
        <Route path="liabilities" element={<Liabilities />} />
        <Route path="vehicles" element={<VehicleFleet />} />
        <Route path="messages" element={<Messages />} />
        <Route path="project-management" element={<ProjectManagement />} />
        <Route path="contractors" element={<ContractorManagement />} />
        <Route path="proposal-builder" element={<ProposalBuilder />} />
        <Route path="proposal-builder/:proposalId" element={<ProposalBuilder />} />
      </Route>

      {/* Public Client Intake Form */}
      <Route path="/intake/:token" element={<ClientIntakeForm />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <ScrollToTop />
        <TranslationProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: '#0A0A0A',
                  color: '#FFFFFF',
                  border: '1px solid #C6A87C',
                },
              }}
            />
          </AuthProvider>
        </TranslationProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
